import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { useDetailDialog } from '$pages/detail/detail-page-provider';
import { isLocalURL } from '$utilities/browser';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const Link = forwardRef((
    /**@type {PropTypes.InferProps<typeof propTypes>} */
    {href, children, ...restProps},
    /**@type {React.ForwardedRef<HTMLAnchorElement>} */
    ref
) => {
    const { openPage } = useDetailDialog();

    /**
     * Check whether event was modified. E.g.: cmd + click
     * @param {React.MouseEvent} event
     * @returns {boolean}
     */
    const isModifiedEvent = (event) => {
        const { target } = event.currentTarget;
        return (
            (target && target !== '_self') ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey || // triggers resource download
            (event.nativeEvent && event.nativeEvent.which === 2)
        );
    };

    /** @param {React.MouseEvent} event */
    const handleClick = (event) => {
        // ignore click for browser’s default behavior
        if (isModifiedEvent(event) || !isLocalURL(href)) {
            return;
        }

        event.preventDefault();

        // Bring up page in modal
        openPage(href);
    };

    return (
        <a
            {...restProps}
            href={href}
            onClick={handleClick}
            ref={ref}>
            {children}
        </a>
    );
});

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    href: PropTypes.string
};

Link.displayName = 'Link';
Link.propTypes = propTypes;

export default Link;
